import Vue from 'vue'
import axios from 'axios'

function apiBaseUrl () {
  const configuredUrl = process.env.VUE_APP_API_URL
  const fallbackPort = process.env.VUE_APP_API_PORT || '5011'

  if (typeof window === 'undefined') {
    return configuredUrl || `http://127.0.0.1:${fallbackPort}/api/v1`
  }

  const host = window.location.hostname
  const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(host)
  const configuredIsLocal = configuredUrl && /\/\/(localhost|127\.0\.0\.1)(:|\/)/.test(configuredUrl)

  if (configuredUrl && (!configuredIsLocal || isLocalHost)) {
    return configuredUrl
  }

  return `${window.location.protocol}//${host}:${fallbackPort}/api/v1`
}

const api = axios.create({
  baseURL: apiBaseUrl()
})

let refreshPromise = null

function readSession () {
  return JSON.parse(localStorage.getItem('complyops.auth') || '{}')
}

function writeSession (payload) {
  const current = readSession()
  const next = {
    ...current,
    accessToken: payload.access_token || current.accessToken,
    refreshToken: payload.refresh_token || current.refreshToken,
    tenantId: payload.tenant ? payload.tenant.id : current.tenantId,
    user: payload.user || current.user,
    tenant: payload.tenant || current.tenant
  }

  localStorage.setItem('complyops.auth', JSON.stringify(next))

  return next
}

function clearSession () {
  localStorage.removeItem('complyops.auth')
}

async function refreshSession () {
  if (!refreshPromise) {
    const saved = readSession()

    if (!saved.refreshToken) {
      return Promise.reject(new Error('Refresh token ausente.'))
    }

    refreshPromise = axios
      .post(`${apiBaseUrl()}/auth/refresh`, { refresh_token: saved.refreshToken })
      .then(({ data }) => writeSession(data))
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

api.interceptors.request.use(config => {
  const saved = readSession()

  if (saved.accessToken) {
    config.headers.Authorization = `Bearer ${saved.accessToken}`
  }

  if (saved.tenantId) {
    config.headers['x-tenant-id'] = saved.tenantId
  }

  return config
})

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config || {}
    const status = error.response ? error.response.status : null
    const url = originalRequest.url || ''
    const isAuthEndpoint = url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout')

    if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error)
    }

    originalRequest._retry = true

    try {
      const session = await refreshSession()
      originalRequest.headers = originalRequest.headers || {}
      originalRequest.headers.Authorization = `Bearer ${session.accessToken}`

      if (session.tenantId) {
        originalRequest.headers['x-tenant-id'] = session.tenantId
      }

      return api(originalRequest)
    } catch (refreshError) {
      clearSession()

      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }

      return Promise.reject(refreshError)
    }
  }
)

Vue.prototype.$api = api

export default api
